import React from "react";
import { motion } from "framer-motion";
import { FaCloud, FaCalendarAlt, FaBullseye, FaLeaf, FaChartLine, FaCogs, FaFileSignature, FaExclamationTriangle } from "react-icons/fa";

function CarbonReductionPlan() {
  return (
    <div className="page-container">
      <motion.section
        className="policy-hero carbon-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="policy-hero-content">
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            Carbon Reduction Plan
          </motion.h1>
          <motion.p
            className="policy-intro"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Supplier name: Neurovasc Ltd — Publication date: 30th January 2025
          </motion.p>
        </div>
      </motion.section>

      {/* Key stats */}
      <section className="policy-content">
        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-icon" aria-hidden="true"><FaCloud /></div>
            <div className="stat-meta">
              <div className="stat-value">2.1</div>
              <div className="stat-label">Scope 1 &amp; 2 (tCO2e)</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" aria-hidden="true"><FaCalendarAlt /></div>
            <div className="stat-meta">
              <div className="stat-value">2024-2025</div>
              <div className="stat-label">Baseline Year</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" aria-hidden="true"><FaBullseye /></div>
            <div className="stat-meta">
              <div className="stat-value">2040</div>
              <div className="stat-label">Net Zero Target</div>
            </div>
          </div>
        </div>
      </section>

      <section className="policy-content">
        <div className="policy-block">
          
            <strong>Date of last review:</strong> 30th January 2025
          
          <h2><span className="section-icon"><FaLeaf /></span>Commitment to achieving Net Zero</h2>
          <p>
            Neurovasc Ltd is committed to achieving Net Zero emissions by
            2040.
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaChartLine /></span>Baseline Emissions Footprint</h2>
          <p>
            Baseline emissions are a record of greenhouse gases produced in the
            past and provide the reference point against which emissions
            reduction will be measured. We implement our carbon reduction
            strategy through two sustainability programmes – ‘Decoupling our
            Carbon’ and ‘Going Circular’. We are committed to aligning our
            operations to “The Carbon Trust Route to Net Zero”.
          </p>

          <p>
            We are setting our net zero 2025 baseline to capture our overall
            business impact, including business travel, operations and related
            activity impact.
          </p>

          <p>
            <strong>Baseline Year:</strong> 2024-2025
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaCloud /></span>Baseline year emissions</h2>
          <p>
            The following describes the approach to our baseline emissions and
            categories. Emissions are expressed in tCO2e.
          </p>

          <h3>Scope 1</h3>
          <p>
            Scope 1 are direct GHG emissions from sources owned or controlled
            by Neurovasc Ltd. We have limited Scope 1 activity — staff work
            remotely and there is minimal office/vehicle use recorded.
          </p>

          <h3>Scope 2</h3>
          <p>
            Scope 2 are indirect emissions from purchased electricity, heat
            or cooling. Example recorded items:
          </p>
          <ul>
            <li>Office purchased electricity — 2,000 kWh (emission factor used)</li>
          </ul>

          <h3>Scope 3</h3>
          <p>
            Scope 3 includes emissions not within Scope 1 or 2 boundary and
            typically represent the majority of an organisation’s total
            emissions. For our service these are derived from manufacture,
            logistics, storage and delivery. We select partners and suppliers
            that adhere to environmental standards (ISO 14001) and collect
            data from them to calculate Scope 3 emissions.
          </p>

          <p>
            <strong>Total Emissions</strong>: Scope 1 and 2 ≈ 2.1 tCO2e plus
            additional Scope 3 emissions dependent on volume and suppliers.
          </p>
        </div>

        <div className="policy-block emissions-table">
          <h2>Emissions Data (baseline year)</h2>
          <p className="small-note">The table below summarises recorded emissions for the baseline year 2024–2025. Scope 3 will be reported once supplier data is collected.</p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Activity / Unit</th>
                  <th>Emissions (tCO2e)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Office electricity</td>
                  <td>2,000 kWh</td>
                  <td>0.424</td>
                </tr>
                <tr>
                  <td>Office heating</td>
                  <td>0 kWh</td>
                  <td>0.000</td>
                </tr>
                <tr>
                  <td>Business travel (recorded)</td>
                  <td>Public transport / minimal</td>
                  <td>0.000</td>
                </tr>
                <tr className="summary-row">
                  <td colSpan="2"><strong>Scope 1 &amp; 2 total</strong></td>
                  <td><strong>2.1</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaCalendarAlt /></span>Current Emissions Reporting</h2>
          <p>
            <strong>Reporting Year:</strong> Jan-2024 — Jan-2025
          </p>
          <p>
            Examples of items recorded include office electricity and public
            transport where used. We are exporting electricity via solar
            generation and continue to prioritise low-carbon ways of working
            such as videocalls rather than in-person meetings.
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaBullseye /></span>Emissions Reduction Targets</h2>
          <p>
            We have committed to becoming net zero by 2040 with near-term
            Science-Based Targets, aligned to a 1.5°C scenario. Our baseline
            year has been reset to 2025. We aim to halve our operational
            footprint by 2028 against the 2025 baseline and work with
            suppliers to embed climate action.
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaCogs /></span>Carbon Reduction Projects</h2>
          <p>
            Completed and planned initiatives include energy management
            measures (LEDs, device power management), supplier carbon impact
            assessments, employee engagement and promoting low-emission
            travel options. We will explore ISO 14001 implementation in 2027
            and offsetting schemes where appropriate.
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaFileSignature /></span>Declaration and Sign Off</h2>
          <p>
            This Carbon Reduction Plan has been completed in accordance with
            PPN 06/21 and associated guidance. Emissions have been reported
            using the GHG Protocol and appropriate Government conversion
            factors. Scope 1 and 2 have been reported per SECR requirements.
          </p>

          <p>
            This plan has been reviewed and signed off by the Managing
            Director.
          </p>

          <p>
            Signed on behalf of Neurovasc Ltd: <br />
            Alper Cetin (Managing Director) <br />
            Date: 18th January 2025
          </p>
        </div>
      </section>
    </div>
  );
}

export default CarbonReductionPlan;
